import React, { useState, useEffect } from 'react';
import { Fab, Box, Typography, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { CameraAlt, Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const FloatingCamera = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isVisible, setIsVisible] = useState(true);
  const [bottom, setBottom] = useState(20);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handlePosition = () => {
      const footer = document.querySelector('.footer');
      if (!footer) {
        setBottom(20);
        return;
      }
      const footerRect = footer.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      // If the footer is visible in the viewport
      if (footerRect.top < windowHeight) {
        // Position 50px above the footer
        setBottom(50);
      } else {
        setBottom(20);
      }
    };

    const handleMenuState = () => {
      const body = document.body;
      setIsMenuOpen(body.classList.contains('menu-open'));
    };

    // Initial check
    handleMenuState();

    // Create a MutationObserver to watch for class changes on body
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          handleMenuState();
        }
      });
    });

    observer.observe(document.body, { attributes: true });

    window.addEventListener('scroll', handlePosition);
    window.addEventListener('resize', handlePosition);
    handlePosition();
    
    return () => {
      window.removeEventListener('scroll', handlePosition);
      window.removeEventListener('resize', handlePosition);
      observer.disconnect();
    };
  }, []);

  const handleCameraClick = () => {
    navigate('/Scanner');
  };

  const handleCloseClick = (e) => {
    e.stopPropagation();
    setIsVisible(false);
  };

  // Hide on laptop/desktop devices
  if (!isMobile || !isVisible || isMenuOpen) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: bottom,
        right: 20,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        transition: 'bottom 0.3s ease'
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={handleCloseClick}
        sx={{
          position: 'absolute',
          top: -8,
          right: -8,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          width: 28,
          height: 28,
          zIndex: 1001,
          border: '1px solid rgba(0, 0, 0, 0.2)',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 1)',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
          }
        }}
      >
        <Close sx={{ fontSize: 18, color: '#666' }} />
      </IconButton>

      {/* Camera button with text */}
      <Fab
        color="primary"
        onClick={handleCameraClick}
        sx={{
          width: 60,
          height: 60,
          backgroundColor: '#1976d2',
          '&:hover': {
            backgroundColor: '#1565c0',
          },
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        }}
      >
        <CameraAlt />
      </Fab>
      
      <Typography
        variant="caption"
        sx={{
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '0.75rem',
          fontWeight: 500,
          textAlign: 'center',
          lineHeight: 1.2,
          maxWidth: '70px'
        }}
      >
        Scan a product
      </Typography>
    </Box>
  );
};

export default FloatingCamera; 